import React, {useCallback, useEffect} from 'react';
import {Chip, Grid, IconButton, Typography} from 'web-library';
import {IconName} from 'web-library/stories/atom/Icon/Icon';

import {useHistory, useParams} from 'react-router-dom';
import {MutationStatus} from 'react-query';
import {UseFormReturn} from 'react-hook-form';
import {ANNOUNCEMENT_FUNCTION_MAP} from 'web-library/models/AuthorityFunctions';
import {UploadedFile} from 'web-library/models/UploadedFile';
import {ConfigurationType} from '../../../api/candidate/config/model';
import {configurationTypeData} from '../../../modules/admin/logic/dropDownOption';
import {prepareFiles} from '../../../utils/UploadUtil';
import {useGetAnnouncementDetails} from '../../../api-refactored/manage-announcements/useGetAnnouncementDetails';
import usePostPresignLinksforOpsAdmin from './usePostPresignLinksforOpsAdmin.';
import {
  CreateAnnouncementRequest,
  usePostCreateAnnouncement,
} from '../../../api-refactored/manage-announcements/usePostCreateAnnouncement';
import {useGetAnnouncementFeature} from '../../../api-refactored/manage-announcements/useGetAnnouncementFeature';
import {useGetAnnouncementCandidateTypes} from '../../../api-refactored/manage-announcements/useGetAnnouncementCandidateTypes';
import {useGetAnnouncementExamLevels} from '../../../api-refactored/manage-announcements/useGetAnnouncementExamLevels';
import {useGetAnnouncementExamCentres} from '../../../api-refactored/manage-announcements/useGetAnnouncementExamCentres';
import RouteKey from '../../../routes/key';
import {
  RecipientSelectionMode,
  RecipientTypeMode,
} from '../../manageNotifications/SendNotification';
import {CreateNewAnnouncementData} from './createNewAnnouncementSchema';
import {
  EditAnnouncementRequest,
  usePutEditAnnouncement,
} from '../../../api-refactored/manage-announcements/usePutEditAnnouncement';
import {AnnouncementConfigResponse} from '../../../api-refactored/manage-announcements/useGetAnnouncementConfig';

const {admin} = RouteKey.child;
const {manageAnnouncements} = RouteKey.admin.child;

interface useCustomSelectDropdownProps {
  classes: Record<string, string>;
}

export const useCustomSelectDropdown = ({classes}: useCustomSelectDropdownProps) => {
  const handleSelectClick = (
      e: any,
      setSelectedCustomSelectValue: React.Dispatch<React.SetStateAction<string[]>>,
      isOpenCustomSelectDropdown: boolean,
      setIsOpenCustomSelectDropdown: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (e.target.value) {
      const sortedArray = [...e.target.value].sort((firstVal, secondVal) => firstVal - secondVal);
      setSelectedCustomSelectValue(sortedArray);
    } else {
    }
  };

  const handleDelete = (
      applicationUuid: string,
      selectedCustomSelectValue: string[],
      setSelectedCustomSelectValue: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelectedCustomSelectValue(
        selectedCustomSelectValue.filter((value) => value !== applicationUuid)
    );
  };

  const handleMenuItemsRender = (
      selected: unknown | string[],
      dropDownArray: ConfigurationType[],
      selectedCustomSelectValue: string[],
      setSelectedCustomSelectValue: React.Dispatch<React.SetStateAction<string[]>>,
      placeholder: string,
      disabled?: boolean
  ) => {
    if (Array.isArray(selected) && selected.length > 0 && dropDownArray) {
      return (
          <Grid container>
            <Grid container xs={11} direction="row" className={classes.chipsContainer}>
              {selected.map((selectedIndex) => {
                const name = configurationTypeData(dropDownArray).find(
                    (option) => option.value === selectedIndex
                )?.children;
                return (
                    <>
                      {name && (
                          <Chip
                              id={name}
                              className={classes.chipLabel}
                              deleteButton={!disabled}
                              onDelete={() =>
                                  !disabled &&
                                  handleDelete(
                                      selectedIndex,
                                      selectedCustomSelectValue,
                                      setSelectedCustomSelectValue
                                  )
                              }
                              label={name}
                              noPadding
                          />
                      )}
                    </>
                );
              })}
            </Grid>
            {!disabled && (
                <IconButton
                    onClick={(event) => handleClearCustomSelect(event, setSelectedCustomSelectValue)}
                    ariaLabel="delete-customSelect-value"
                    className={classes.iconButton}
                    color="secondary"
                    name={IconName.crossCircle16}
                />
            )}
          </Grid>
      );
    }

    return <Typography>{placeholder}</Typography>;
  };

  const handleClearCustomSelect = (
      event: any,
      setSelectedCustomSelectValue: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    event.stopPropagation();
    setSelectedCustomSelectValue([]);
  };

  return {
    handleSelectClick,
    handleDelete,
    handleMenuItemsRender,
    handleClearCustomSelect,
  };
};

interface CreateEditAnnouncementProps {
  featureValue: string;
  files: UploadedFile[];
  candidateTypeValue: string[];
  examLevelValue: string[] | null;
  examCentreValue: string[];
  titleText: string;
  descriptionText: string;
  isEdit: boolean;
  shouldSubmit: boolean;
  priorityNoticeCheckbox: boolean;
  startDate: string;
  endDate: string;
  setRecipientSelectionMode: React.Dispatch<React.SetStateAction<RecipientSelectionMode>>;
  setRecipientTypeMode: React.Dispatch<React.SetStateAction<RecipientTypeMode>>;
  setFeatureValue: React.Dispatch<React.SetStateAction<string>>;
  setShouldSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  setExamLevelValue: React.Dispatch<React.SetStateAction<string[]>>;
  setExamCentreValue: React.Dispatch<React.SetStateAction<string[]>>;
  setIsRefetchAnnouncementExamCentres: React.Dispatch<React.SetStateAction<boolean>>;
  setTitleText: React.Dispatch<React.SetStateAction<string>>;
  setDescriptionText: React.Dispatch<React.SetStateAction<string>>;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  setCandidateTypeValue: React.Dispatch<React.SetStateAction<string[]>>;
  setPriorityNoticeCheckbox: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFetchedAnnouncementData: React.Dispatch<React.SetStateAction<boolean>>;
  setTrimmedDescLength: React.Dispatch<React.SetStateAction<number>>;
  recipientSelectionMode: RecipientSelectionMode;
  recipientTypeMode: RecipientTypeMode;
  formHook: UseFormReturn<CreateNewAnnouncementData>;
  announcementConfig?: AnnouncementConfigResponse;
  displayDescriptionError: boolean;
  trimmedDescLength: number;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
}

export const useCreateEditAnnouncement = ({
                                            featureValue,
                                            files,
                                            candidateTypeValue,
                                            examLevelValue,
                                            examCentreValue,
                                            titleText,
                                            descriptionText,
                                            isEdit,
                                            shouldSubmit,
                                            priorityNoticeCheckbox,
                                            startDate,
                                            endDate,
                                            setRecipientSelectionMode,
                                            setRecipientTypeMode,
                                            setFeatureValue,
                                            setShouldSubmit,
                                            setFiles,
                                            setDraftFiles,
                                            setApiButtonStatus,
                                            setExamLevelValue,
                                            setCandidateTypeValue,
                                            setStartDate,
                                            setEndDate,
                                            setTitleText,
                                            setDescriptionText,
                                            setExamCentreValue,
                                            setPriorityNoticeCheckbox,
                                            setIsFetchedAnnouncementData,
                                            setTrimmedDescLength,
                                            recipientSelectionMode,
                                            recipientTypeMode,
                                            formHook,
                                            announcementConfig,
                                            displayDescriptionError,
                                            trimmedDescLength,
                                            setErrorMessage,
                                          }: CreateEditAnnouncementProps) => {
  const history = useHistory<{ featureValue: string; page?: number; pageSize?: number }>();

  const {uuid} = useParams<{ uuid: string }>();

  const dataFromListing = history?.location?.state;

  const handleBackToListing = () =>
      history.push(`/${admin}/${manageAnnouncements.route}`, {
        featureValue,
        page: dataFromListing.page,
        pageSize: dataFromListing.pageSize,
      });

  const usePostPresignLinksMutation = usePostPresignLinksforOpsAdmin({
    files,
    setFiles,
    setShouldSubmit,
    setApiButtonStatus,
  });

  const {
    mutate: mutatePostCreateAnnouncement,
    status: createAnnouncementStatus,
  } = usePostCreateAnnouncement({
    onSuccess: handleBackToListing,
    onError: (error) => {
      setApiButtonStatus('idle');
      setErrorMessage(error?.response?.data?.message!);
    },
  });

  const {mutate: mutatePutEditAnnouncement} = usePutEditAnnouncement({
    onSuccess: handleBackToListing,
    onError: (error) => {
      setApiButtonStatus('idle');
      setErrorMessage(error?.response?.data?.message!);
    },
    uuid,
  });

  const {data: announcementFeature} = useGetAnnouncementFeature({
    onSuccess: (res) => {
      if (!featureValue) {
        setFeatureValue(res[0].code);
      }
    },
  });

  const {data: announcementCandidateTypes} = useGetAnnouncementCandidateTypes({
    onSuccess: (res) => {
      if (!isEdit) {
        setCandidateTypeValue([`${res[0].code}`]);
      }
    },
  });
  const {data: announcementExamLevels} = useGetAnnouncementExamLevels({
    functionName: ANNOUNCEMENT_FUNCTION_MAP[featureValue],
    onSuccess: (res) => {
      if (res.length && !isEdit) {
        setExamLevelValue([`${res[0].code}`]);
      }
    },
  });

  const {
    refetch: refetchAnnouncementExamCentres,
    data: announcementExamCentres,
  } = useGetAnnouncementExamCentres({
    onSuccess: (res) => {
      if (!isEdit) {
        setExamCentreValue([`${res[0].code}`]);
      }
    },
    enabled: !!RecipientTypeMode.Coordinator,
  });

  const {
    data: AnnouncementDetails,
    isLoading: isLoadingAnnouncementDetails,
  } = useGetAnnouncementDetails({
    uuid,
    enabled: isEdit,
    onSuccess: (res) => {
      setTitleText(res.title);
      setDescriptionText(res.description);
      if (featureValue !== 'BROADCAST_MESSAGE') {
        setTrimmedDescLength(res.description.trim().length);
      }
      setStartDate(res.startDate);
      setEndDate(res.endDate);
      formHook.reset({
        announcementTitle: res.title,
        startDate: res.startDate,
        endDate: res.endDate,
      });
      setFeatureValue(res.announcementType);
      setPriorityNoticeCheckbox(res.priority);
      setRecipientSelectionMode(
          res.recipientSelection.everyone
              ? RecipientSelectionMode.Everyone
              : RecipientSelectionMode.SelectedRecipients
      );
      setRecipientTypeMode(
          res.recipientSelection.candidateRecipient
              ? RecipientTypeMode.Candidate
              : RecipientTypeMode.Coordinator
      );
      setExamCentreValue(res.recipientSelection.examCentres || []);
      setExamLevelValue(res.recipientSelection.examLevels || []);
      setCandidateTypeValue(res.recipientSelection.candidateTypes || []);
      const attachFiles = res?.fileInfo?.map((f) => {
        const newFile = new File([new ArrayBuffer(f.fileSizeInBytes)], f.fileName);
        return {
          file: newFile,
          hasError: false,
          pending: false,
          uploaded: true,
          key: f.fileUuid,
        };
      });
      setDraftFiles(attachFiles || []);
      setIsFetchedAnnouncementData(true);
    },
  });

  useEffect(() => setApiButtonStatus(createAnnouncementStatus), [
    createAnnouncementStatus,
    setApiButtonStatus,
  ]);

  const createAnnouncement = useCallback(
    (fileUuids: string[]) => {
      const mutatePostCreateAnnouncementData: CreateAnnouncementRequest = {
        title: titleText,
        description: descriptionText,
        priority: priorityNoticeCheckbox,
        startDate: startDate,
        endDate: endDate,
        announcementType: featureValue,
        fileUuids,
        recipientSelection: {
          everyone: recipientSelectionMode === RecipientSelectionMode.Everyone,
          candidateRecipient: recipientTypeMode === RecipientTypeMode.Candidate,
          candidateTypes:
            recipientTypeMode === RecipientTypeMode.Candidate && candidateTypeValue.length
              ? candidateTypeValue
              : null,
          examLevels: recipientTypeMode === RecipientTypeMode.Candidate ? examLevelValue : null,
          examCentres: recipientTypeMode === RecipientTypeMode.Coordinator ? examCentreValue : null,
        },
      };
      if (
        featureValue === 'BROADCAST_MESSAGE' ||
        featureValue === 'ADVISORY_BANNER' ||
        featureValue === 'PAYMENT_MESSAGE'
      ) {
        delete mutatePostCreateAnnouncementData.recipientSelection;
      }
      if (!announcementConfig?.titleEnabled) {
        delete mutatePostCreateAnnouncementData.title;
      }
      if (!announcementConfig?.descriptionEnabled) {
        delete mutatePostCreateAnnouncementData.description;
      }
      if (!announcementConfig?.priorityEnabled) {
        delete mutatePostCreateAnnouncementData.priority;
      }
      if (!announcementConfig?.recipientSelectionEnabled) {
        delete mutatePostCreateAnnouncementData.recipientSelection;
      }
      if (!announcementConfig?.validityPeriodEnabled) {
        delete mutatePostCreateAnnouncementData.startDate;
        delete mutatePostCreateAnnouncementData.endDate;
      }
      mutatePostCreateAnnouncement(mutatePostCreateAnnouncementData);
    },
    [
      announcementConfig,
      candidateTypeValue,
      descriptionText,
      endDate,
      examCentreValue,
      examLevelValue,
      featureValue,
      mutatePostCreateAnnouncement,
      priorityNoticeCheckbox,
      recipientTypeMode,
      startDate,
      titleText,
      recipientSelectionMode,
    ]
  );

  const editAnnouncement = useCallback(
    (fileUuids: string[]) => {
      const mutatePutEditAnnouncementData: EditAnnouncementRequest = {
        title: titleText,
        description: descriptionText,
        priority: priorityNoticeCheckbox,
        startDate: startDate,
        endDate: endDate,
        announcementType: featureValue,
        fileUuids,
        recipientSelection: {
          everyone: recipientSelectionMode === RecipientSelectionMode.Everyone,
          candidateRecipient: recipientTypeMode === RecipientTypeMode.Candidate,
          candidateTypes:
            recipientTypeMode === RecipientTypeMode.Candidate && candidateTypeValue.length
              ? candidateTypeValue
              : null,
          examLevels: recipientTypeMode === RecipientTypeMode.Candidate ? examLevelValue : null,
          examCentres: recipientTypeMode === RecipientTypeMode.Coordinator ? examCentreValue : null,
        },
      };
      if (
        featureValue === 'BROADCAST_MESSAGE' ||
        featureValue === 'ADVISORY_BANNER' ||
        featureValue === 'PAYMENT_MESSAGE'
      ) {
        delete mutatePutEditAnnouncementData.recipientSelection;
      }
      mutatePutEditAnnouncement(mutatePutEditAnnouncementData);
    },
    [
      candidateTypeValue,
      descriptionText,
      endDate,
      examCentreValue,
      examLevelValue,
      featureValue,
      mutatePutEditAnnouncement,
      priorityNoticeCheckbox,
      recipientTypeMode,
      startDate,
      titleText,
      recipientSelectionMode,
    ]
  );

  useEffect(() => {
    const isAllFilesUploaded = files.every((file) => file.uploaded);
    const fileUploadHasError = files.some((file) => file.hasError);
    const fileUuids: string[] = [];
    files.forEach((file) => fileUuids.push(file.key || ''));

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (!displayDescriptionError && isAllFilesUploaded && !isEdit && shouldSubmit) {
      createAnnouncement(fileUuids);
      setShouldSubmit(false);
    }
    if (!displayDescriptionError && isAllFilesUploaded && isEdit && shouldSubmit) {
      editAnnouncement(fileUuids);
      setShouldSubmit(false);
    }
  }, [
    candidateTypeValue,
    createAnnouncement,
    descriptionText,
    displayDescriptionError,
    editAnnouncement,
    endDate,
    examCentreValue,
    examLevelValue,
    featureValue,
    files,
    isEdit,
    mutatePostCreateAnnouncement,
    priorityNoticeCheckbox,
    recipientTypeMode,
    setShouldSubmit,
    shouldSubmit,
    startDate,
    titleText,
  ]);
  const isDescriptionRequired =
    !!announcementConfig?.descriptionEnabled &&
    !['BROADCAST_MESSAGE', 'ADVISORY_BANNER'].includes(featureValue);
  const stripRichText = (s = '') =>
    s
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

  const handleConfirm = () => {
    const canSubmit =
        (!displayDescriptionError &&
            (announcementConfig?.descriptionEnabled ? !!trimmedDescLength : true) &&
            featureValue !== 'BROADCAST_MESSAGE') ||
        featureValue === 'BROADCAST_MESSAGE';

    if (!canSubmit) return;

    setShouldSubmit(canSubmit);
    const {filenames} = prepareFiles(files, setFiles);
    setApiButtonStatus('loading');

    if (filenames.length) {
      usePostPresignLinksMutation.mutate({
        applicationType: 'ANNOUNCEMENT',
        filenames,
        uuid,
      });
    }
  };
  return {
    announcementFeature,
    announcementCandidateTypes,
    announcementExamLevels,
    announcementExamCentres,
    refetchAnnouncementExamCentres,
    isLoadingAnnouncementDetails,
    handleConfirm,
    handleBackToListing,
    AnnouncementDetails,
  };
};
